import { useState, type ReactNode } from 'react'
import { Plus, Trash2, Server as ServerIcon, HardDrive, Lock, EyeOff } from 'lucide-react'
import { type StructureData, type Server, type Share, deriveUncPath } from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Input,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

export function ServersTab({ data, update }: Props) {
  const a = useAnonymize()
  const [selectedServerId, setSelectedServerId] = useState<string | null>(
    data.servers[0]?.id ?? null,
  )
  const [confirmDialog, setConfirmDialog] = useState<
    | {
        title: string
        message: ReactNode
        confirmLabel?: string
        onConfirm: () => void
      }
    | null
  >(null)

  function addServer() {
    const s: Server = {
      id: crypto.randomUUID(),
      name: 'Neuer Server',
      hostname: '',
      description: '',
      shares: [],
    }
    update({ servers: [...data.servers, s] })
    setSelectedServerId(s.id)
  }

  function updateServer(id: string, patch: Partial<Server>) {
    update({
      servers: data.servers.map((s) => {
        if (s.id !== id) return s
        const merged = { ...s, ...patch }
        if (patch.hostname !== undefined && patch.hostname !== s.hostname) {
          merged.shares = s.shares.map((sh) => ({
            ...sh,
            uncPath: deriveUncPath(merged.hostname || merged.name, sh.name, sh.isHidden),
          }))
        }
        return merged
      }),
    })
  }

  function deleteServer(id: string) {
    const srv = data.servers.find((s) => s.id === id)
    const shareIds = new Set(srv?.shares.map((sh) => sh.id) ?? [])
    const folderIds = new Set(
      data.folders.filter((f) => shareIds.has(f.shareId)).map((f) => f.id),
    )
    update({
      servers: data.servers.filter((s) => s.id !== id),
      drives: data.drives.filter((d) => !shareIds.has(d.shareId)),
      folders: data.folders.filter((f) => !shareIds.has(f.shareId)),
      permissions: data.permissions.filter((p) => {
        if (p.shareId && shareIds.has(p.shareId)) return false
        if (p.folderId && folderIds.has(p.folderId)) return false
        return true
      }),
    })
    if (selectedServerId === id) setSelectedServerId(null)
  }

  function requestDeleteServer(id: string) {
    const srv = data.servers.find((s) => s.id === id)
    if (!srv) return
    const shareIds = new Set(srv.shares.map((sh) => sh.id))
    const folderCount = data.folders.filter((f) => shareIds.has(f.shareId)).length
    const driveCount = data.drives.filter((d) => shareIds.has(d.shareId)).length
    setConfirmDialog({
      title: 'Server löschen',
      confirmLabel: 'Server löschen',
      message: (
        <div className="space-y-2">
          <p>
            Soll der Server <b>{srv.name || '(ohne Name)'}</b> wirklich gelöscht werden?
          </p>
          <ul className="text-caption text-gray-600 list-disc list-inside space-y-0.5">
            <li>{srv.shares.length} Freigabe(n)</li>
            <li>{folderCount} Ordner</li>
            <li>{driveCount} Laufwerks-Mapping(s)</li>
            <li>Alle Berechtigungen dieser Freigaben</li>
          </ul>
          <p className="text-caption text-red-600 font-medium">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>
      ),
      onConfirm: () => {
        deleteServer(id)
        setConfirmDialog(null)
      },
    })
  }

  function requestDeleteShare(shareId: string) {
    if (!selected) return
    const sh = selected.shares.find((x) => x.id === shareId)
    if (!sh) return
    const folderCount = data.folders.filter((f) => f.shareId === shareId).length
    const driveCount = data.drives.filter((d) => d.shareId === shareId).length
    setConfirmDialog({
      title: 'Freigabe löschen',
      confirmLabel: 'Freigabe löschen',
      message: (
        <div className="space-y-2">
          <p>
            Soll die Freigabe <b>{sh.name || '(ohne Name)'}</b> wirklich gelöscht werden?
          </p>
          <ul className="text-caption text-gray-600 list-disc list-inside space-y-0.5">
            <li>{folderCount} Ordner</li>
            <li>{driveCount} Laufwerks-Mapping(s)</li>
            <li>Alle Berechtigungen dieser Freigabe</li>
          </ul>
          <p className="text-caption text-red-600 font-medium">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>
      ),
      onConfirm: () => {
        deleteShare(shareId)
        setConfirmDialog(null)
      },
    })
  }

  const selected = data.servers.find((s) => s.id === selectedServerId) ?? null

  function addShare() {
    if (!selected) return
    const name = 'NEW_SHARE'
    const isHidden = true
    const sh: Share = {
      id: crypto.randomUUID(),
      name,
      isHidden,
      uncPath: deriveUncPath(selected.hostname || selected.name, name, isHidden),
      description: '',
      allowedGroupIds: [],
    }
    updateServer(selected.id, { shares: [...selected.shares, sh] })
  }

  function updateShare(shareId: string, patch: Partial<Share>) {
    if (!selected) return
    updateServer(selected.id, {
      shares: selected.shares.map((sh) => {
        if (sh.id !== shareId) return sh
        const merged = { ...sh, ...patch }
        const nameOrHiddenChanged =
          (patch.name !== undefined && patch.name !== sh.name) ||
          (patch.isHidden !== undefined && patch.isHidden !== sh.isHidden)
        if (nameOrHiddenChanged && patch.uncPath === undefined) {
          merged.uncPath = deriveUncPath(
            selected.hostname || selected.name,
            merged.name,
            merged.isHidden,
          )
        }
        return merged
      }),
    })
  }

  function deleteShare(shareId: string) {
    if (!selected) return
    const folderIdsInShare = new Set(
      data.folders.filter((f) => f.shareId === shareId).map((f) => f.id),
    )
    updateServer(selected.id, { shares: selected.shares.filter((sh) => sh.id !== shareId) })
    update({
      drives: data.drives.filter((d) => d.shareId !== shareId),
      folders: data.folders.filter((f) => f.shareId !== shareId),
      permissions: data.permissions.filter((p) => {
        if (p.shareId === shareId) return false
        if (p.folderId && folderIdsInShare.has(p.folderId)) return false
        return true
      }),
    })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
      <Card>
        <Card.Header>
          <Card.Title className="text-body">Server</Card.Title>
          <IconButton
            icon={<Plus className="w-4 h-4" />}
            aria-label="Server hinzufügen"
            variant="ghost"
            size="sm"
            onClick={addServer}
            title="Server hinzufügen"
          />
        </Card.Header>
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {data.servers.length === 0 && (
            <EmptyState size="sm" icon={<ServerIcon />} title="Noch keine Server" />
          )}
          {data.servers.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedServerId(s.id)}
              className={cn(
                'w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors',
                selectedServerId === s.id && 'bg-brand-50',
              )}
            >
              <ServerIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-body font-medium text-gray-900 truncate">{a.token(s.name)}</p>
                <p className="text-caption text-gray-500 truncate">
                  {s.hostname ? a.token(s.hostname) : '(kein Hostname)'}
                </p>
                <p className="text-caption text-gray-400">{s.shares.length} Freigabe(n)</p>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {selected ? (
        <div className="space-y-4">
          <Card>
            <Card.Header>
              <Card.Title className="text-body">Server-Details</Card.Title>
              <IconButton
                icon={<Trash2 className="w-4 h-4" />}
                aria-label="Server löschen"
                variant="ghost"
                size="sm"
                onClick={() => requestDeleteServer(selected.id)}
              />
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Name"
                  type="text"
                  value={a.token(selected.name)}
                  onChange={(e) => {
                    if (!a.enabled) updateServer(selected.id, { name: e.target.value })
                  }}
                  readOnly={a.enabled}
                />
                <Input
                  label="Hostname / FQDN"
                  type="text"
                  value={a.token(selected.hostname)}
                  onChange={(e) => {
                    if (!a.enabled) updateServer(selected.id, { hostname: e.target.value })
                  }}
                  readOnly={a.enabled}
                  placeholder="z.B. srv01.kunde.local"
                  className="font-mono"
                />
                <Input
                  label="Beschreibung"
                  wrapperClassName="md:col-span-2"
                  type="text"
                  value={a.freeText(selected.description ?? '')}
                  onChange={(e) => {
                    if (!a.enabled) updateServer(selected.id, { description: e.target.value })
                  }}
                  readOnly={a.enabled}
                  placeholder="z.B. Fileserver, Software-X-Server…"
                />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title className="text-body flex items-center gap-2">
                <HardDrive className="w-4 h-4" />Freigaben
              </Card.Title>
              <Button
                variant="ghost"
                size="xs"
                leadingIcon={<Plus className="w-3.5 h-3.5" />}
                onClick={addShare}
              >
                Freigabe
              </Button>
            </Card.Header>
            {selected.shares.length === 0 ? (
              <EmptyState size="sm" title="Noch keine Freigaben auf diesem Server" />
            ) : (
              <DataTable
                data={selected.shares}
                getRowId={(sh) => sh.id}
                enableSorting={false}
                columns={[
                  {
                    accessorKey: 'name',
                    header: 'Name',
                    cell: ({ row }) => (
                      <Input
                        type="text"
                        value={a.token(row.original.name)}
                        onChange={(e) => {
                          if (!a.enabled)
                            updateShare(row.original.id, { name: e.target.value.replace(/\$+$/, '') })
                        }}
                        readOnly={a.enabled}
                        placeholder="z.B. GEW"
                        className="font-mono text-caption py-1"
                      />
                    ),
                  },
                  {
                    accessorKey: 'isHidden',
                    header: 'Versteckt ($)',
                    size: 90,
                    cell: ({ row }) => (
                      <span className="inline-flex items-center justify-center gap-1">
                        <Checkbox
                          checked={row.original.isHidden}
                          onChange={(e) => updateShare(row.original.id, { isHidden: e.target.checked })}
                        />
                        {row.original.isHidden && <EyeOff className="w-3 h-3 text-gray-400" />}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'uncPath',
                    header: 'UNC-Pfad',
                    cell: ({ row }) => (
                      <Input
                        type="text"
                        value={a.token(row.original.uncPath)}
                        onChange={(e) => {
                          if (!a.enabled) updateShare(row.original.id, { uncPath: e.target.value })
                        }}
                        readOnly={a.enabled}
                        className="font-mono text-caption py-1"
                      />
                    ),
                  },
                  {
                    id: 'access',
                    header: 'Zugriff',
                    size: 90,
                    cell: ({ row }) => {
                      const sh = row.original
                      return (sh.allowedGroupIds?.length ?? 0) > 0 ? (
                        <Badge
                          variant="warning"
                          size="sm"
                          title={`Nur: ${sh.allowedGroupIds!
                            .map((id) =>
                              a.token(data.groups.find((g) => g.id === id)?.name ?? '?'),
                            )
                            .join(', ')}`}
                        >
                          <Lock className="w-3 h-3" />
                          {sh.allowedGroupIds!.length}
                        </Badge>
                      ) : (
                        <span className="text-caption text-gray-400">offen</span>
                      )
                    },
                  },
                  {
                    id: 'actions',
                    header: '',
                    enableSorting: false,
                    size: 50,
                    cell: ({ row }) => (
                      <IconButton
                        icon={<Trash2 className="w-3.5 h-3.5" />}
                        aria-label="Freigabe löschen"
                        variant="ghost"
                        size="xs"
                        onClick={() => requestDeleteShare(row.original.id)}
                      />
                    ),
                  },
                ]}
              />
            )}
            {selected.shares.length > 0 && (
              <Card.Footer className="text-caption text-gray-500 block space-y-0.5">
                <p>
                  Zugriffsbeschränkung pro Freigabe kann im Tab <b>Zugriffsmatrix</b> konfiguriert
                  werden.
                </p>
                <p>Laufwerksbuchstaben werden im Tab <b>Laufwerke</b> gepflegt.</p>
              </Card.Footer>
            )}
          </Card>
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<ServerIcon />}
            title="Server auswählen oder neu anlegen"
          />
        </Card>
      )}

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        variant="danger"
        onConfirm={() => confirmDialog?.onConfirm()}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  )
}
